import React, { FunctionComponent, useCallback, useMemo } from 'react';

import PSHeader from '@ps-int/ps-header/src';
import {
    GlobalBarControl,
    GlobalBar as GlobalBarLocal,
    MonetizationItem as MonetizationItemLocal,
} from '@yandex-360/global-bar';
import { Settings as SettingsIcon } from '@yandex-360/iconostasis/dist/icons/Settings';
import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme/ScopedTheme';
import { PressEvent } from '@yandex-360/orb-components/dist/types';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { RootState } from '../../../../init-store';
import {
    getExperimentsFlags,
    isLightTheme,
    selectGlobalBarInitData,
    selectIsGlobalBarEnabled,
    selectSelectedLogin,
    selectSelectedUid,
} from '../../features/config/selectors';
import { selectIsCorpHeaderEnabled, selectIsTestplane } from '../../features/flags/selectors';
import { ModuleFederationComponents } from '../../features/module-federation/constants';
import { getModuleFederation } from '../../features/module-federation/helpers/get-module-federation';
import { openSettingsModalAction } from '../../features/settings-modal/slice';
import { toPassport } from '../../features/user';
import { getLogoOrganization, isYandexSsoUserSelector } from '../../features/user/selectors';
import { withErrorBoundary } from '../../helpers/hocs/with-error-boundary';
import { i18n } from '../../helpers/i18n';
import { countMetrika, countMetrikaGlobalBar } from '../../helpers/metrika';
import { useActions } from '../../hooks';
import Button from '../button';
import { Popover } from '../popover';
import { CORP_LINKS, getLinks, getLinksOverride, normalizeRetpath } from './helpers';
import css from './index.css';

const mapState = (state: RootState) => {
    const {
        user,
        ua: { isMobile, BrowserName },
        config: {
            errorPageType,
            appLang,
            yandexuid,
            avatarsOrigin,
            userIdUrl,
            isOnPremise,
            tld,
            yaServices,
            currentHost,
            geoTimezone,
            environment,
            globalBarOnPremServices,
        },
    } = state;
    return {
        user,
        isMobile,
        yandexuid,
        avatarsOrigin,
        userIdUrl,
        isOnPremise,
        tld,
        yaServices,
        lang: appLang,
        currentHost,
        geoTimezone,
        BrowserName,
        showFan: Boolean(user?.isB2b),
        isLightTheme: isLightTheme(state),
        errorPageType,
        environment,
        globalBarOnPremServices,
        isTestplane: selectIsTestplane(state),
        expFlags: getExperimentsFlags(state),
    };
};

const mapDispatch = { toPassport };

const connector = connect(mapState, mapDispatch);

type HeaderConnectedProps = ConnectedProps<typeof connector> & { hidden?: boolean };

const HeaderComponent: FunctionComponent<HeaderConnectedProps> = ({
    lang,
    tld,
    yaServices,
    user,
    isMobile,
    yandexuid,
    avatarsOrigin,
    userIdUrl,
    isOnPremise,
    currentHost,
    geoTimezone,
    toPassport,
    BrowserName,
    hidden,
    showFan,
    isLightTheme,
    errorPageType,
    environment,
    globalBarOnPremServices,
    isTestplane,
    expFlags,
}) => {
    const handleLoginClick = useCallback(() => toPassport(), [toPassport]);

    const actions = useActions({ openSettingsModalAction });
    const onClickSettings = (event: PressEvent) => {
        actions.openSettingsModalAction({
            isHiddenSectionVisible: event.altKey,
        });
    };

    const loginButton = useMemo(
        () => (
            <Button testId="login-button" view="neutral" size="md" onClick={handleLoginClick}>
                {i18n('common', 'login')}
            </Button>
        ),
        [handleLoginClick]
    );

    const globalBarInitData = useSelector(selectGlobalBarInitData);
    const isGlobalBarEnabled = useSelector(selectIsGlobalBarEnabled);
    const isYandexSso = useSelector(isYandexSsoUserSelector);
    const isCorpHeaderEnabled = useSelector(selectIsCorpHeaderEnabled);
    const shouldUseCorpBar = Boolean(isYandexSso || (isCorpHeaderEnabled && user?.isYandexTeamUser));

    const { pathname, search } = useLocation();
    const enabledNoB2cTariffPromo = user?.enabledNoB2cTariffPromo;

    const logoOrganization = useSelector(getLogoOrganization);
    const selectedUid = useSelector(selectSelectedUid);
    const selectedLogin = useSelector(selectSelectedLogin);

    const isLivePage = pathname.startsWith('/live/');

    const forceDarkTheme = isLivePage && !errorPageType;
    const theme = isLightTheme && !forceDarkTheme ? 'light' : 'dark';
    const shouldFetchMail = !isOnPremise && !isYandexSso;
    const userIdHost = userIdUrl ? new URL(userIdUrl).host : undefined;
    const avatarHost = new URL(avatarsOrigin).host;

    const retpath = `https://${currentHost}${pathname}${search}`;

    const moduleFederation = getModuleFederation();

    if (isGlobalBarEnabled) {
        const globalBarModule = moduleFederation?.getComponent<ModuleFederationComponents.GlobalBar>(
            ModuleFederationComponents.GlobalBar
        );
        const GlobalBar = globalBarModule?.GlobalBar ?? GlobalBarLocal;
        const MonetizationItem = globalBarModule?.MonetizationItem ?? MonetizationItemLocal;
        return (
            <ScopedTheme theme={theme}>
                <GlobalBar
                    initData={globalBarInitData}
                    is669Sid={user?.isYandexTeamUser}
                    isSsoYandexTeam={user?.isYandexSsoUser}
                    preferCorp={isCorpHeaderEnabled}
                    currentServiceLink="/"
                    isB2bAdmin={Boolean(user?.isConnectAdmin)}
                    isB2bUser={Boolean(user?.isB2b)}
                    currentService="Telemost"
                    tld={tld}
                    lang={lang}
                    timeZone={user?.timezone || geoTimezone}
                    uid={user?.uid}
                    userProps={
                        user
                            ? {
                                  colorScheme: theme,
                                  userData: {
                                      avatarId: user.avatarId,
                                      plus: user.hasPlus,
                                  },
                                  retpath: normalizeRetpath(retpath),
                                  env: ['testing', 'development'].includes(environment) ? 'test' : 'prod',
                                  queryParams: {
                                      origin: 'b2b360',
                                  },
                                  host: userIdHost,
                                  avatarHost,
                                  // В OnPrem нет доступа к cloud Mail user-id counters, поэтому
                                  // счётчик непрочитанных писем оставляем только в cloud-режиме.
                                  fetchMail: shouldFetchMail,
                                  platform: isMobile ? 'touch' : 'desktop',
                              }
                            : undefined
                    }
                    className={cn(css.globalBar, { [css.hidden]: hidden })}
                    countMetrikaFn={countMetrikaGlobalBar}
                    hideTuning={enabledNoB2cTariffPromo}
                    bottomSlot2={
                        enabledNoB2cTariffPromo || shouldUseCorpBar ? null : (
                            <MonetizationItem autoplay={!user?.hasProSubscription && !isTestplane} />
                        )
                    }
                    bottomSlot1={
                        isLivePage ? null : (
                            <GlobalBarControl
                                testId="settings-button"
                                tooltipContent={i18n('settings', 'title')}
                                onPress={onClickSettings}
                            >
                                <SettingsIcon variant="solid" size="md" />
                            </GlobalBarControl>
                        )
                    }
                    onLoginClick={handleLoginClick}
                    features={expFlags}
                    allowedServices={isOnPremise ? globalBarOnPremServices : undefined}
                    linksOverride={getLinksOverride(yaServices, shouldUseCorpBar, selectedUid, selectedLogin)}
                />
            </ScopedTheme>
        );
    }

    const MFHeader =
        moduleFederation?.getComponent<ModuleFederationComponents.PSHeader>(ModuleFederationComponents.PSHeader)
            ?.default ?? PSHeader;

    return (
        <ScopedTheme theme={theme}>
            <MFHeader
                links={getLinks(yaServices, shouldUseCorpBar)}
                corpLinks={CORP_LINKS}
                yandex360Link={
                    enabledNoB2cTariffPromo || user?.isB2b || user?.hasProSubscription ? '/' : yaServices.yandex360
                }
                allServicesLink={yaServices.all}
                isB2bAdmin={Boolean(user?.isConnectAdmin)}
                isB2bUser={Boolean(user?.isB2b)}
                isPDD={Boolean(user?.hosted)}
                currentService="Telemost"
                tld={tld}
                customLogoUrl={logoOrganization}
                lang={lang}
                timeZone={user?.timezone || geoTimezone}
                userProps={
                    user
                        ? {
                              uid: user.uid,
                              name: user.name,
                              avatarId: user.avatarId,
                              yu: yandexuid,
                              avatarHost,
                              retpath,
                              yaplusAvailable: !enabledNoB2cTariffPromo,
                              hasPlus: user.hasPlus,
                              env: ['testing', 'development'].includes(environment) ? 'test' : 'prod',
                              queryParams: {
                                  origin: 'b2b360',
                              },
                              // В OnPrem нет доступа к cloud Mail user-id counters, поэтому
                              // счётчик непрочитанных писем там всегда выключен.
                              noCounter: !shouldFetchMail,
                              platform: isMobile ? 'touch' : 'desktop',
                          }
                        : undefined
                }
                noUserName
                LegoPopup={Popover}
                LegoButton={Button}
                rightContent={!user && loginButton}
                theme={isLightTheme && !forceDarkTheme ? 'light' : 'dark'}
                className={cn({ [css.PSHeader_hidden]: hidden }, { [css.mobilePsHeader]: isMobile })}
                countMetrikaFn={countMetrika}
                // в IE не работает интерактивность, так как под него не траспайлим бандл
                hideMore={BrowserName === 'MSIE'}
                showDocuments
                tuning={enabledNoB2cTariffPromo ? 'none' : 'on-top'}
                showFan={showFan}
                showBoards={!isYandexSso}
                useLegoV3
                useUserID
                isOrbTheme
                isNewIcons
            />
        </ScopedTheme>
    );
};

export const Header = withErrorBoundary(connector(HeaderComponent));
